import {useFrame} from '@react-three/fiber';
import {useEffect, useState} from 'react';
import {TrainingTarget} from '../../components/app/TrainingTarget';
import {VR_POSITIONS, createTargetPosition, getDistanceValue, getRangeValue} from '../../constants/vrPositions';
import {ballPositionStore, useXRController} from '../../hooks/useXRController';
import {useStore} from '../../store/useStore';
import {ExerciseSettings} from '../../types/exercises';
import {logger} from '../../utils/logger';

export const SaccadesExercise: React.FC<ExerciseSettings> = ({
  eyeToTrain = 'both',
  isRedGreen = false,
  isMFBF = false,
  isOverlay = false,
  leftEyeColor,
  rightEyeColor,
  leftEyeOverlay,
  rightEyeOverlay,
  speed = 7,
  range = 'medium',
  distance = 'middle',
  ballPosition,
}) => {
  const initialDistance = getDistanceValue(distance);
  const initialPosition = createTargetPosition(0, VR_POSITIONS.EXERCISE.TARGET_HEIGHT, initialDistance);

  const [targetPosition, setTargetPosition] = useState(initialPosition);
  const [isInitialized, setIsInitialized] = useState(false);
  const [time, setTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const toggleTimerPause = useStore(state => state.toggleTimerPause);

  const {isInXR, handlePositionChange} = useXRController();

  const onMenuToggle = (isOpen: boolean) => {
    setIsPaused(isOpen);
    toggleTimerPause(isOpen);
    logger.info(`SaccadesExercise: Menu toggled, global pause set to ${isOpen}`);
  };

  const getSaccadeSpeedValue = (speedValue: number): number => {
    const minInterval = 1.0;
    const maxInterval = 3.0;
    return maxInterval - (speedValue / 15) * (maxInterval - minInterval);
  };

  const targetDistance = getDistanceValue(distance);

  useEffect(() => {
    logger.info('SaccadesExercise mounted');
    setIsInitialized(true);
    return () => {
      logger.info('SaccadesExercise unmounted');
    };
  }, []);

  // Initialize ball position from saved settings
  useEffect(() => {
    if (ballPosition) {
      logger.info('Initializing ball position from saved settings:', ballPosition);
      ballPositionStore.setOffset(ballPosition);
    }
  }, [ballPosition]);

  useFrame((_, delta) => {
    if (!isInitialized || isPaused) {
      return;
    }

    setTime(prev => prev + delta);

    // Calculate movement values using current props
    const saccadeInterval = getSaccadeSpeedValue(speed);
    const radius = getRangeValue(range);

    const angle = Math.floor(time / saccadeInterval) * (Math.PI / 2);
    const xOffset = Math.cos(angle) * radius;
    const yOffset = Math.sin(angle) * radius;
    const newPosition = createTargetPosition(xOffset, VR_POSITIONS.EXERCISE.TARGET_HEIGHT + yOffset, targetDistance);
    setTargetPosition(newPosition);
  });

  const getPeripheralPositions = () => {
    const positions = [];
    const radius = getRangeValue(range);
    const peripheralRadius = radius * 2;
    const count = 4;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const x = Math.cos(angle) * peripheralRadius;
      const y = Math.sin(angle) * peripheralRadius;
      positions.push(createTargetPosition(x, VR_POSITIONS.EXERCISE.TARGET_HEIGHT + y, targetDistance));
    }
    return positions;
  };

  return (
    <group>
      <TrainingTarget
        position={targetPosition}
        showPeripheral={false}
        eyeToTrain={eyeToTrain}
        isRedGreen={isRedGreen}
        isMFBF={isMFBF}
        isOverlay={isOverlay}
        leftEyeColor={leftEyeColor}
        rightEyeColor={rightEyeColor}
        leftEyeOverlay={leftEyeOverlay}
        rightEyeOverlay={rightEyeOverlay}
        inXRSession={isInXR}
        onPositionChange={handlePositionChange}
        onMenuToggle={onMenuToggle}
      />

      {isMFBF &&
        getPeripheralPositions().map((pos, index) => (
          <TrainingTarget
            key={index}
            position={pos}
            scale={0.5}
            showPeripheral={true}
            eyeToTrain={eyeToTrain}
            isRedGreen={isRedGreen}
            isMFBF={isMFBF}
            isOverlay={isOverlay}
            inXRSession={isInXR}
          />
        ))}
    </group>
  );
};
